import { Link } from "react-router-dom";

import { dashboard as dashboardApi } from "../api/resources";
import {
  Card,
  EmptyState,
  ErrorBanner,
  Loading,
  PageHeader,
  Progress,
  Stat,
} from "../components/ui";
import { useCurrentOrganization } from "../lib/OrganizationContext";
import { percent } from "../lib/format";
import { useAsync } from "../lib/useAsync";

export function DashboardPage() {
  const { organizationId } = useCurrentOrganization();
  const { data, error, loading } = useAsync(
    () =>
      organizationId
        ? dashboardApi.organization(organizationId)
        : Promise.resolve(undefined as never),
    [organizationId],
  );

  if (!organizationId) {
    return (
      <>
        <PageHeader
          title="Dashboard"
          description="Discover how the company works: ask the right people, capture what they say with evidence, and propose what matters to Enterprise Context."
        />
        <Card>
          <EmptyState
            title="No organization yet"
            description="Create an organization to begin discovery. Nothing is assumed about a company until someone tells us or a document shows us."
            action={
              <Link className="btn btn--primary" to="/onboarding/new">
                Start discovery
              </Link>
            }
          />
        </Card>
      </>
    );
  }

  if (loading) return <Loading />;
  if (error) return <ErrorBanner error={error} />;
  if (!data) return null;

  const reviewed = data.verified_facts + data.rejected_facts;
  const modules = Object.entries(data.completion_detail?.modules ?? {});
  const firstRun = data.knowledge_facts === 0 && data.files_uploaded === 0;

  // A brand-new organization gets three clear choices, not twenty empty counters.
  if (firstRun) {
    return (
      <>
        <PageHeader
          title={`Let's get started with ${data.organization_name}`}
          description="Three ways in. You can do them in any order, and stop and come back at any point."
        />
        <div className="stack">
          <Card>
            <div className="start-choices">
              <Link className="start-choice" to="/onboarding/new">
                <span className="start-choice__step">Start here</span>
                <strong>Run the onboarding interview</strong>
                <span>
                  Pick the areas that apply to this company and answer as much
                  as you can. About 20 minutes for the essentials.
                </span>
              </Link>
              <Link className="start-choice" to="/uploads">
                <span className="start-choice__step">Fastest</span>
                <strong>Upload what you already have</strong>
                <span>
                  A product master, a location list, an SOP or a retailer guide.
                  Whatever we can read, we stop asking about.
                </span>
              </Link>
              <Link className="start-choice" to="/interviews">
                <span className="start-choice__step">Later</span>
                <strong>Interview the people who do the work</strong>
                <span>
                  Department and employee interviews surface the parts that live
                  in people's heads.
                </span>
              </Link>
            </div>
          </Card>

          <Card title="What happens to your answers">
            <ul className="checklist">
              <li>
                <span>1.</span>
                <span>
                  Everything you tell us, and every file you upload, is kept as
                  evidence.
                </span>
              </li>
              <li>
                <span>2.</span>
                <span>
                  We turn it into plain statements — "NetSuite is where stock is
                  counted" — and show you where each one came from.
                </span>
              </li>
              <li>
                <span>3.</span>
                <span>
                  Nothing counts as true until you confirm it. Anything we
                  cannot work out is listed as still missing rather than guessed
                  at.
                </span>
              </li>
            </ul>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Discovery overview"
        description={
          data.active_project_name
            ? `Active onboarding: ${data.active_project_name}`
            : "No active onboarding project yet."
        }
        actions={
          <>
            <Link className="btn" to="/interviews">
              Interviews
            </Link>
            <Link className="btn btn--primary" to="/onboarding/new">
              Start discovery
            </Link>
          </>
        }
      />

      <div className="stack">
        <Card title="Onboarding completion">
          <div style={{ maxWidth: 520, marginBottom: 14 }}>
            <Progress value={data.completion_percentage} />
          </div>
          <div className="grid grid--stats">
            {Object.entries(data.completion_detail?.components ?? {}).map(
              ([name, detail]) => (
                <Stat
                  key={name}
                  label={name.replace(/_/g, " ")}
                  value={`${detail.done}/${detail.total}`}
                  hint={percent(detail.ratio * 100)}
                />
              ),
            )}
          </div>
        </Card>

        <div className="grid grid--stats">
          <Stat
            label="Departments"
            value={data.departments_total}
            hint={`${data.departments_interviewed} interviewed`}
          />
          <Stat
            label="People"
            value={data.people_total}
            hint={`${data.people_interviewed} interviewed`}
          />
          <Stat
            label="Files uploaded"
            value={data.files_uploaded}
            hint={`${data.files_parsed} parsed`}
          />
          <Stat label="Systems discovered" value={data.systems_discovered} />
          <Stat
            label="Workflows discovered"
            value={data.workflows_discovered}
          />
          <Stat label="Integrations" value={data.integrations_discovered} />
          <Stat label="Warehouses" value={data.warehouses_discovered} />
          <Stat
            label="Retailers / channels"
            value={data.retailers_discovered}
          />
        </div>

        <div className="grid grid--stats">
          <Stat label="Knowledge facts" value={data.knowledge_facts} />
          <Stat
            label="Human-confirmed observations"
            value={data.verified_facts}
            tone="ok"
            hint="A person agreed this is what they said"
          />
          <Stat
            label="Awaiting confirmation"
            value={data.unverified_facts}
            tone={data.unverified_facts ? "warn" : undefined}
            hint="Proposed — awaiting review"
          />
          <Stat
            label="Reviewed"
            value={reviewed}
            hint="Confirmed or rejected"
          />
          <Stat
            label="Open questions"
            value={data.open_questions}
            tone={data.open_questions ? "warn" : undefined}
          />
          <Stat
            label="Knowledge gaps"
            value={data.knowledge_gaps}
            tone={data.gaps_by_severity?.required ? "danger" : undefined}
            hint={`${data.gaps_by_severity?.required ?? 0} required`}
          />
          <Stat
            label="Contradictions"
            value={data.open_contradictions}
            tone={data.open_contradictions ? "danger" : undefined}
            hint={`${data.contradictions} total`}
          />
        </div>

        <div className="grid grid--two">
          <Card title="What needs attention">
            <ul className="checklist">
              <li>
                <strong>{data.unverified_facts}</strong>
                <span>
                  proposed facts need confirmation —{" "}
                  <Link to="/knowledge">review knowledge</Link>
                </span>
              </li>
              <li>
                <strong>{data.gaps_by_severity?.required ?? 0}</strong>
                <span>
                  required things we still do not know —{" "}
                  <Link to="/gaps">see gaps</Link>
                </span>
              </li>
              <li>
                <strong>{data.open_contradictions}</strong>
                <span>
                  unresolved contradictions —{" "}
                  <Link to="/gaps">resolve conflicts</Link>
                </span>
              </li>
              <li>
                <strong>{data.open_questions}</strong>
                <span>
                  follow-up questions raised by rules —{" "}
                  <Link to="/gaps">open questions</Link>
                </span>
              </li>
            </ul>
          </Card>

          <Card title="Module progress">
            {modules.length === 0 ? (
              <p className="muted">No modules selected yet.</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Module</th>
                      <th className="numeric">Required answered</th>
                      <th className="numeric">Open gaps</th>
                      <th style={{ width: 160 }}>Progress</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modules.map(([key, detail]) => (
                      <tr key={key}>
                        <td>{detail.name}</td>
                        <td className="numeric">
                          {detail.required_answered}/{detail.required_questions}
                        </td>
                        <td className="numeric">{detail.open_gaps}</td>
                        <td>
                          <Progress value={detail.percentage} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}
